import { Chat, ChatMessage } from "myfitworld-model";
import { LastMessageData } from "myfitworld-model/src";
import { firestore } from "src/firebase";
import { getUserByUserId } from "./usersApi";

export const subscribeToCountUnreadMessages = (
    { userId, onUnreadUserCountChange, onUnreadTotalCountChange }: { 
        userId: string; 
        onUnreadUserCountChange: (unreadMessages: Map<string, number>) => void,
        onUnreadTotalCountChange: (unreadCount: number) => void
    }
  ) => {
    return firestore.collectionGroup("messages")
        .where('threadId', '==', userId)
        .where('read', '==', false)
        .onSnapshot((snap) => {
          let unreadMessages = new Map<string, number>();
            if (!snap.empty) {
                onUnreadTotalCountChange(snap.docs.length);
                snap.docs.forEach(doc => {
                    const senderId = doc.data().senderId;
                    const currentCount = unreadMessages.get(senderId) || 0;
                    unreadMessages.set(senderId, currentCount + 1);
                });
                onUnreadUserCountChange(unreadMessages);
            } else {
                onUnreadTotalCountChange(0);
                onUnreadUserCountChange(unreadMessages); 
            }
        });
};

export const subscribeToChats = (
  userId: string,
  onUpdate: (chats: Chat[]) => void,
  onCatchError: (error: unknown) => void // pass error so you can show specific messages if needed
) => {
  try {
    return firestore
      .collection("chats")
      .where("watchers", "array-contains", userId)
      .orderBy("lastMessageData.createdAt", "desc")
      .onSnapshot(
        (querySnap) => {
          if (querySnap.empty) {
            onUpdate([]);
            return;
          }
          const chats: Chat[] = querySnap.docs.map((doc) => formatChat(doc));
          onUpdate(chats);
        },
        (error) => {
          console.error("Error listening to chats:", error);
          onCatchError(error); // ✅ call the provided error handler
        }
      );
  } catch (error) {
    console.error("Unexpected error in subscribeToChats:", error);
    onCatchError(error); // ✅ catch sync issues
  }
};


export const subscribeToMessagesForChat = (chatIds: string[], limit: number, onUpdate: (messages: ChatMessage[]) => void, onCatchError: (error: unknown) => void ) => {
  try {
    return firestore
      .collectionGroup("messages")
      .where("chatId", "in", chatIds)
      .orderBy("createdAt", "desc")
      .limit(limit)
      .onSnapshot(
        (querySnap) => {
          if (querySnap.empty) {
            onUpdate([]);
            return;
          }
          const messages: ChatMessage[] = querySnap.docs.map((doc) => formatMessage(doc));
          onUpdate(messages);
        },
        (error) => {
          console.error("Error listening to chats:", error);
          onCatchError(error); // ✅ call the provided error handler
        }
      );
  } catch (error) {
    console.error("Unexpected error in subscribeToChats:", error);
    onCatchError(error); // ✅ catch sync issues
  }
};


  export const fetchMessages = async ({
    chatIds,
    limit,
    startAfter
  }: {
    chatIds: string[],
    limit: number,
    startAfter?: string
  }) => {
    try {
      console.log(chatIds);
      let query = firestore.collectionGroup("messages").where("chatId", "in", chatIds).orderBy("createdAt", "desc").limit(limit); 
  
      const snap = await query.get();
      const messages = snap.docs.reverse().map((doc) => formatMessage(doc));
      console.log(messages);
      return messages;
  
    } catch (error) {
      throw error;
    }
  };

  export const fetchOlderMessages = async (chatId: string, startAfter: string, limit: number) => {
    try {
      let query = firestore.collectionGroup("messages").where("chatId", "==", chatId).orderBy("createdAt", "desc");
      const startAfterQuery = firestore.collection("chats").doc(chatId).collection("messages").doc(startAfter);
      const startAfterDoc = await startAfterQuery.get();
      if (startAfterDoc.exists) {
          query = query.startAfter(startAfterDoc);
      }
      query = query.limit(limit);
  
      const snap = await query.get();
      const messages = snap.docs.map((doc) => formatMessage(doc));
      return messages;
  
    } catch (error) {
      throw error;
    }
  }

  export const addNewMessage = async (message: ChatMessage, chatId: string) => {
    try {
      const batch = firestore.batch();
      const chatRef = firestore.collection('chats').doc(chatId);
      const messagesRef = chatRef.collection('messages').doc();
      const lastMessageData: LastMessageData = {
        createdAt: message.createdAt,
        text: message.text,
        read: message.read,
        senderId: message.senderId
      };
      batch.set(messagesRef, message);
      batch.update(chatRef, {lastMessageData});

      await batch.commit();
    } catch (error) {
      throw error;
    }
  }

  export const createChatAndAddNewMessage = async (message: ChatMessage) => {
    try {
      console.log(`${message.threadId}-${message.senderId}`)
      const batch = firestore.batch();
      const chatRef = firestore.collection('chats').doc(`${message.threadId}-${message.senderId}`);
      const messagesRef = chatRef.collection('messages').doc();
      const userFirstData = await formatChatUserData(message.threadId);
      const userSecondData = await formatChatUserData(message.senderId);
      const lastMessageData: LastMessageData = {
        createdAt: message.createdAt,
        text: message.text,
        read: message.read,
        senderId: message.senderId
      };
      const chat: Chat = {
        userFirstData,
        userSecondData,
        watchers: [message.senderId, message.threadId],
        lastMessageData
      };

      batch.set(messagesRef, {...message, chatId: `${message.threadId}-${message.senderId}`});
      batch.set(chatRef, chat);
      await batch.commit();
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  export const readMessages = async (messagesForReadingByChatId: Map<string, string[]>) => {
    try {
      const batch = firestore.batch();
      for (const [key, value] of messagesForReadingByChatId) {
        const chatRef = firestore.collection("chats").doc(key);
        for (const messageForReadingId of value) {
          const messageRef = chatRef.collection("messages").doc(messageForReadingId);
          batch.update(messageRef, { read: true });
        }
        batch.update(chatRef, { 'lastMessageData.read': true });
      }
  
      await batch.commit();
      return true;
  
    } catch (error) {
      console.error("Error updating messages as read:", error);
      throw error;
    }
  }
  
const formatChat = (doc: firebase.firestore.DocumentData) => {
    return {
      ...doc.data(),
      id: doc.id,
      lastMessageData: {
        ...doc.data().lastMessageData,
        createdAt: doc.data().lastMessageData.createdAt.toDate(), 
      },
      userFirstData: {
        ...doc.data().userFirstData,
        ...(doc.data().userFirstData.deletedDate && {
          deletedDate: doc.data().userFirstData.deletedDate.toDate()
        }),
      },
      userSecondData: {
        ...doc.data().userSecondData,
        ...(doc.data().userSecondData.deletedDate && {
          deletedDate: doc.data().userSecondData.deletedDate.toDate()
        }),
      }
    } as Chat;
};

const formatMessage = (doc: firebase.firestore.DocumentData) => {
  return {
    ...doc.data(),
    id: doc.id,
    createdAt: doc.data().createdAt.toDate(), 
  } as ChatMessage;
}

const formatChatUserData = async (userId: string) => {
  const userData = await getUserByUserId(userId);
  return {
    id: userData.id,
    name: `${userData.firstName} ${userData.lastName}`,
    deleted: false,
    archived: false,
    ...(userData.avatarURL && { avatarURL: userData.avatarURL })
  };
};